import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { usePlatformAuth, platformApi } from "@/context/PlatformAuthContext";
import { ShieldCheck, Lock, KeyRound, ArrowRight } from "lucide-react";

export default function PlatformLogin() {
  const { token, setSession } = usePlatformAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("password");  // password | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  if (token) return <Navigate to="/platform-admin" replace />;

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await platformApi().post("/auth/login", { email, password });
      if (r.data?.mfa_required) {
        setTicket(r.data.mfa_ticket);
        setStep("mfa");
      } else {
        setSession(r.data.token, r.data.admin);
        nav("/platform-admin", { replace: true });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await platformApi().post("/auth/login/mfa", { mfa_ticket: ticket, code });
      setSession(r.data.token, r.data.admin);
      nav("/platform-admin", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4" data-testid="platform-login">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Ayobell</div>
            <div className="font-heading font-semibold text-lg">Platform Administration</div>
          </div>
        </div>

        {step === "password" ? (
          <form onSubmit={submitPassword} className="space-y-3" data-testid="platform-login-password-form">
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400">Email</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="platform-login-email"
                className="w-full pl-9 pr-3 h-10 rounded-md bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:outline-none text-sm"
                placeholder="platform@ayobell.com"
              />
            </div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400">Password</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="platform-login-password"
                className="w-full pl-9 pr-3 h-10 rounded-md bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:outline-none text-sm"
              />
            </div>
            {err && <div className="text-xs text-rose-400" data-testid="platform-login-error">{err}</div>}
            <button
              type="submit" disabled={busy}
              data-testid="platform-login-submit"
              className="w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"
            >
              {busy ? "Signing in…" : "Continue"} <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="text-[10px] text-slate-500 pt-2 leading-relaxed">
              Restricted to Ayobell Platform Administrators. Customer accounts must use the main application.
            </div>
          </form>
        ) : (
          <form onSubmit={submitMfa} className="space-y-3" data-testid="platform-login-mfa-form">
            <div className="text-xs text-emerald-400 font-semibold uppercase tracking-widest">Two-Factor Verification</div>
            <div className="text-sm text-slate-300">Enter the 6-digit code from your authenticator app.</div>
            <input
              type="text" required autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={8}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="platform-login-mfa-code"
              className="w-full h-12 rounded-md bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:outline-none text-center text-2xl tracking-widest font-mono"
              placeholder="123456"
            />
            {err && <div className="text-xs text-rose-400" data-testid="platform-login-error">{err}</div>}
            <button
              type="submit" disabled={busy}
              data-testid="platform-login-mfa-submit"
              className="w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold"
            >{busy ? "Verifying…" : "Verify & sign in"}</button>
            <button
              type="button" onClick={() => { setStep("password"); setCode(""); setTicket(null); }}
              className="w-full h-8 text-xs text-slate-400 hover:text-slate-200"
            >← Back</button>
          </form>
        )}
      </div>
    </div>
  );
}
